import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { performLogoutAndRedirect } from '../../utils/logout';
import { isFullAdmin } from '../../utils/adminIds';
import BellhoptLogo from './BellhoptLogo';
import {
  FiHome,
  FiPackage,
  FiTruck,
  FiUsers,
  FiSettings,
  FiLogOut,
  FiChevronLeft,
  FiChevronRight,
  FiBell,
  FiClipboard,
  FiGrid,
  FiLayers,
  FiDollarSign,
  FiMessageSquare,
  FiZap,
  FiActivity,
  FiAlertTriangle,
  FiClock,
  FiCpu,
  FiShare2,
} from 'react-icons/fi';

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { adminInfo } = useSelector((state) => state.login);
  const role = adminInfo?.role;
  const fullAdmin = isFullAdmin(adminInfo);

  const handleLogout = () => {
    performLogoutAndRedirect(dispatch);
  };

  // Command Center is now the first entry for everyone and is the default
  // post-login destination. "Ticket Admin" is the old admin tickets screen,
  // renamed and moved to the Management section so it stops shadowing the new
  // hub under the same label.
  const mainNavItems = fullAdmin ? [
    { icon: FiGrid, label: 'Command Center', path: '/command-center' },
    { icon: FiHome, label: 'Dashboard', path: '/dashboard' },
    { icon: FiActivity, label: 'Live Orders', path: '/orders/live' },
    { icon: FiZap, label: 'Dispatch', path: '/dispatch' },
    { icon: FiMessageSquare, label: 'Inquiries', path: '/inquiries' },
  ] : [
    { icon: FiGrid, label: 'Command Center', path: '/command-center' },
    { icon: FiZap, label: 'Dispatch', path: '/dispatch' },
    { icon: FiActivity, label: 'Live Orders', path: '/orders/live' },
    { icon: FiMessageSquare, label: 'Inquiries', path: '/inquiries' },
    { icon: FiTruck, label: 'Concierge Team', path: '/driverList' },
  ];

  const managementNavItems = fullAdmin ? [
    { icon: FiClipboard, label: 'Ticket Admin', path: '/tickets/admin' },
    { icon: FiUsers, label: 'Property Hosts', path: '/hostList' },
    { icon: FiTruck, label: 'Concierge Team', path: '/driverList' },
    { icon: FiClock, label: 'Schedules', path: '/schedules' },
    { icon: FiBell, label: 'Announcements', path: '/announcements' },
    { icon: FiBell, label: 'Guest Push', path: '/guest-push' },
    { icon: FiUsers, label: 'Guest Engagement', path: '/guest-engagement' },
    { icon: FiClipboard, label: 'Concierge Services', path: '/concierge-services' },
    { icon: FiClipboard, label: 'Order Status', path: '/manage-order-status' },
    { icon: FiPackage, label: 'Orders', path: '/orderList' },
    { icon: FiDollarSign, label: 'Assign Store', path: '/assign-property-store' },
    { icon: FiDollarSign, label: 'Delivery Fee', path: '/assign-additional-delivery' },
    { icon: FiDollarSign, label: 'Discounts', path: '/discounts' },
    { icon: FiLayers, label: 'Service Requests', path: '/service-requests' },
    { icon: FiUsers, label: 'Providers', path: '/providers' },
    { icon: FiGrid, label: 'Markets', path: '/markets' },
    { icon: FiDollarSign, label: 'Subscriptions', path: '/subscriptions' },
    { icon: FiCpu, label: 'AI Concierge', path: '/ai-concierge' },
    { icon: FiShare2, label: 'Social', path: '/social' },
  ] : [
    { icon: FiClipboard, label: 'Order Status', path: '/manage-order-status' },
    { icon: FiPackage, label: 'Orders', path: '/orderList' },
    { icon: FiUsers, label: 'Property Hosts', path: '/hostList' },
    { icon: FiTruck, label: 'Concierge Team', path: '/driverList' },
    { icon: FiClock, label: 'Schedules', path: '/schedules' },
  ];

  const bottomNavItems = [
    { icon: FiSettings, label: 'Settings', path: '/settings' },
    { icon: FiAlertTriangle, label: 'Escalate', path: '/escalate' },
  ];

  const NavItem = ({ icon: Icon, label, path }) => {
    const isActive = location.pathname === path || location.pathname.startsWith(path + '/');
    
    return (
      <NavLink
        to={path}
        className={`
          group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200
          ${isActive 
            ? 'bg-indigo-50 text-indigo-600 font-medium' 
            : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
          }
          ${isCollapsed ? 'justify-center' : ''}
        `}
        title={isCollapsed ? label : undefined}
      >
        <div className={`
          flex items-center justify-center w-8 h-8 rounded-lg transition-colors
          ${isActive ? 'bg-indigo-100 text-indigo-600' : 'text-zinc-400 group-hover:text-zinc-600'}
        `}>
          <Icon className="w-[18px] h-[18px]" />
        </div>
        {!isCollapsed && (
          <span className="text-sm whitespace-nowrap">{label}</span>
        )}
        {isActive && !isCollapsed && (
          <div className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-500" />
        )}
      </NavLink>
    );
  };

  const NavSection = ({ title, items }) => (
    <div className="space-y-1">
      {!isCollapsed && (
        <h3 className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
          {title}
        </h3>
      )}
      {items.map((item) => (
        <NavItem key={item.path} {...item} />
      ))}
    </div>
  );

  return (
    <aside 
      className={`
        flex flex-col h-screen bg-white border-r border-zinc-200 transition-all duration-300 ease-spring
        ${isCollapsed ? 'w-[72px]' : 'w-[260px]'}
      `}
    >
      {/* Header */}
      <div className={`
        flex items-center h-16 border-b border-zinc-100 px-4
        ${isCollapsed ? 'justify-center' : 'justify-between'}
      `}>
        {!isCollapsed ? (
          <div className="flex items-center gap-3">
            <BellhoptLogo variant="mark" size="md" />
            <div>
              <h1 className="font-bold text-zinc-900 text-[15px] tracking-tight">Bellhopt</h1>
              <p className="text-[10px] text-zinc-400 font-medium">Operations</p>
            </div>
          </div>
        ) : (
          <BellhoptLogo variant="mark" size="md" />
        )}
        
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={`
            p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors
            ${isCollapsed ? 'hidden' : ''}
          `}
        >
          <FiChevronLeft className="w-4 h-4" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
        <NavSection title="Main" items={mainNavItems} />
        {fullAdmin && <NavSection title="Management" items={managementNavItems} />}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-zinc-100 p-3 space-y-1">
        {bottomNavItems.map((item) => (
          <NavItem key={item.path} {...item} />
        ))}
        
        {/* Logout */}
        <button
          onClick={handleLogout}
          className={`
            w-full group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200
            text-zinc-600 hover:bg-red-50 hover:text-red-600
            ${isCollapsed ? 'justify-center' : ''}
          `}
          title={isCollapsed ? 'Logout' : undefined}
        >
          <div className="flex items-center justify-center w-8 h-8 rounded-lg text-zinc-400 group-hover:text-red-500 transition-colors">
            <FiLogOut className="w-[18px] h-[18px]" />
          </div>
          {!isCollapsed && (
            <span className="text-sm">Logout</span>
          )}
        </button>
      </div>

      {/* User Profile Card */}
      <div className={`
        border-t border-zinc-100 p-3
        ${isCollapsed ? 'px-2' : ''}
      `}>
        <div className={`
          flex items-center gap-3 p-2 rounded-xl bg-zinc-50 hover:bg-zinc-100 transition-colors cursor-pointer
          ${isCollapsed ? 'justify-center p-2' : ''}
        `}
          onClick={() => navigate('/profile')}
        >
          <div className="relative">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-semibold text-sm shadow-md">
              {adminInfo?.firstName?.[0]}{adminInfo?.lastName?.[0]}
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
          </div>
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-900 truncate">
                {adminInfo?.firstName} {adminInfo?.lastName}
              </p>
              <p className="text-xs text-zinc-500 capitalize">{role || 'Staff'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Collapse Toggle (when collapsed) */}
      {isCollapsed && (
        <div className="p-3 border-t border-zinc-100">
          <button
            onClick={() => setIsCollapsed(false)}
            className="w-full p-2 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors flex items-center justify-center"
          >
            <FiChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;

